import { isEmpty } from 'lodash';

export const MODAL_STEPS = {
  NEW_LISTING: 'new_listing',
  SUGGESTED_LISTING: 'suggested_listing',
  LISTING_NOT_FOUND: 'listing_not_found',
};

export const SUBMIT_ACTIONS = {
  SEARCH_OR_CREATE: 'search_or_create',
  VIEW_PROPOSAL: 'view_proposal',
};

export const SALE_OR_LEASE = {
  SALE: 'sale',
  LEASE: 'lease',
};

export const parseNumericValue = (value) => {
  if (!value) return null;
  const parsed = parseFloat(String(value).replace(/[^0-9.]/g, ''));
  return isNaN(parsed) ? null : parsed;
};

// Conflicting fields are pre-filled with the first non-blank value (see mergeDocumentResults). The conflict
// metadata is still preserved on mergeResult.conflicts for the upcoming interactive resolution UI.
export function buildInitialFormState(mergeResult, countryList) {
  const data = mergeResult?.mergedFormData || {};
  const fieldValue = (fieldName, value) => (value || '');

  const defaultCountry = countryList.find(c => c.name === 'United States') || countryList[0];
  const country = data.country_id
    ? countryList.find(c => c.id === Number(data.country_id)) || defaultCountry
    : defaultCountry;

  const state = data.state || country?.countryStates?.[0]?.code || '';

  /* eslint-disable camelcase */
  return {
    address: fieldValue('address', data.address),
    city: fieldValue('city', data.city),
    state,
    zip: fieldValue('zip', data.zip),
    county: fieldValue('county', data.county),
    country,
    sale_or_lease: fieldValue('sale_or_lease', data.sale_or_lease),
    name: fieldValue('name', data.name),
    location_description: fieldValue('location_description', data.location_description),
    property_type_id: fieldValue('property_type_id', data.property_type_id),
    building_size: fieldValue('building_size', data.building_size),
    building_class: fieldValue('building_class', data.building_class),
    sale_price: fieldValue('sale_price', data.sale_price),
    lease_rate: fieldValue('lease_rate', data.lease_rate),
    lot_size: fieldValue('lot_size', data.lot_size),
    number_of_units: fieldValue('number_of_units', data.number_of_units),
    zoning: fieldValue('zoning', data.zoning),
    year_built: fieldValue('year_built', data.year_built),
    year_renovated: fieldValue('year_renovated', data.year_renovated),
    occupancy: fieldValue('occupancy', data.occupancy),
    generate_sale_description: false,
    generate_sale_bullets: false,
    generate_lease_description: false,
    generate_lease_bullets: false,
  };
  /* eslint-enable camelcase */
}

/* eslint-disable camelcase */
export function computeActionRequiredFields({ formState, brokers, defaultBrokerId }) {
  const { address, city, state, zip, country, property_type_id, sale_or_lease } = formState;
  const isStateRequired = country?.countryStates?.length > 0;
  const isZipRequired = !!country?.zipLabel;

  const fields = new Set();
  if (!address) fields.add('address');
  if (!city) fields.add('city');
  if (!Object.prototype.hasOwnProperty.call(country ?? {}, 'id')) fields.add('country_id');
  if (isStateRequired && !state) fields.add('state');
  if (isZipRequired && !zip) fields.add('zip');
  if (!property_type_id) fields.add('property_type_id');
  if (!defaultBrokerId && brokers.some(b => isEmpty(b))) fields.add('broker_name');
  if (!sale_or_lease) fields.add('sale_or_lease');
  return fields;
}
/* eslint-enable camelcase */

export function findMatchingBroker(brokerSelectOptions, brokerName) {
  if (!brokerName || !brokerSelectOptions?.length) return null;
  return brokerSelectOptions.find(
    b => b.label?.toLowerCase() === brokerName.toLowerCase()
  ) || null;
}

export const buildFullAddress = ({ address, city, state, zip }) => `${address}, ${city}, ${state} ${zip || ''}`;
